/**
 * The iPad's All mode: every chart of a layout in the layout's own arrangement.
 *
 * The desktop's grid with the screener's track taken out, exactly as the desktop
 * takes out a hidden screener (`withoutScreener`) — so the charts keep the shares
 * the user dragged them to. Pure, because the component that uses it holds
 * SciChart surfaces and cannot be tested. See
 * [mobile.md](../../../../docs/features/mobile.md#ipad-all-and-full).
 */

import type { Layout } from "@qufolio/model";

import { withoutScreener } from "./layout-view";

export interface ChartGrid {
  /** `grid-template-rows`. */
  rows: string;
  /** `grid-template-columns`. */
  columns: string;
  /** Where each chart section goes, keyed by section id. */
  cells: ReadonlyMap<string, { gridRow: string; gridColumn: string }>;
}

/** Sizes as stored, or equal shares when there are none or they do not fit. */
function template(sizes: readonly string[] | undefined, count: number): string {
  return sizes && sizes.length === count ? sizes.join(" ") : `repeat(${count}, 1fr)`;
}

export function chartGrid(layout: Layout): ChartGrid {
  const shown = withoutScreener(layout);
  const cells = new Map<string, { gridRow: string; gridColumn: string }>();
  for (const section of shown.sections) {
    if (section.kind !== "chartGroup") continue;
    cells.set(section.id, {
      gridRow: `${section.row + 1} / span ${section.rowSpan ?? 1}`,
      gridColumn: `${section.column + 1} / span ${section.columnSpan ?? 1}`,
    });
  }
  return {
    rows: template(shown.rowSizes, shown.rows),
    columns: template(shown.columnSizes, shown.columns),
    cells,
  };
}
